// A choice of views over one table (Admin → Customer assignments: Needs confirming, Assignments, No
// owner): a bordered group of buttons, the chosen one filled, each with its count as a small badge.
// The pages' own tabs (Shipping, Pre-route, Admin) stay underlined tabs: they are pages, not views.

import { cn } from "@/lib/utils";

export interface Segment<T extends string> {
  id: T;
  label: string;
  n: number;
  /** Amber while above 0: something waits there. */
  warn?: boolean;
}

export function SegmentedViews<T extends string>({
  segments,
  value,
  onChange,
  label,
  testId,
}: {
  segments: readonly Segment<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  testId?: string;
}) {
  return (
    <div
      className="bg-background inline-flex flex-wrap rounded-md border p-0.5"
      role="tablist"
      aria-label={label}
      data-testid={testId}
    >
      {segments.map((s) => {
        const on = s.id === value;
        return (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={on}
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-3 py-1 text-sm",
              on ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-muted",
            )}
            onClick={() => onChange(s.id)}
            data-testid={testId ? `${testId}-${s.id}` : undefined}
          >
            {s.label}
            <span
              className={cn(
                "rounded-full px-1.5 text-[11px] leading-4 tabular-nums",
                s.warn && s.n > 0
                  ? "bg-amber-100 font-medium text-amber-800"
                  : on
                    ? "bg-primary-foreground/20"
                    : "bg-muted",
              )}
            >
              {s.n.toLocaleString("en-US")}
            </span>
          </button>
        );
      })}
    </div>
  );
}
